import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../ui/modal.tsx";
import { SearchableSelect } from "../ui/searchableSelect.tsx";
import { buildReportTimezoneOptions } from "../../util/timezoneOptions.ts";
import { getClientTimeZone } from "../../util/clientEnv.ts";
import { cancelExport, exportDataGrid, getExportStatus } from "../../api/endpoints.ts";
import { deleteOwnExport, filenameFromContentDisposition } from "../../api/fileUpload.ts";
import { buildApiUrl, buildAuthHeaders } from "../../api/client.ts";
import { useSession } from "../../hooks/useSession.ts";
import { useAlertDialog } from "../../hooks/useAlertDialog.tsx";
import { BuildStates, ParamType } from "../../types/grid.ts";
import { incompleteExportMessage, incompleteExportRows, isIncompleteExport } from "../../util/incompleteData.ts";
import type { InputParam, SortOrder, ExportDataFileType, ExportInfo } from "../../types/grid.ts";
import styles from "./dataGrid.module.scss";

/**
 * Cultures offered for export formatting (number/date separators). The server
 * accepts ANY valid .NET culture (`new CultureInfo(value)`), of which there are
 * ~800 under .NET's ICU data — far too many to list. This is a curated set of
 * common business locales; the selector is searchable, so add here as needed.
 * `value` must be a valid .NET culture name.
 */
export const CULTURES = [
  // Most-used first (shown at the top before typing).
  { value: "en-US", label: "English (United States)" },
  { value: "sv-SE", label: "Swedish (Sweden)" },
  { value: "en-GB", label: "English (United Kingdom)" },
  { value: "de-DE", label: "German (Germany)" },
  { value: "fr-FR", label: "French (France)" },
  { value: "es-ES", label: "Spanish (Spain)" },
  { value: "nb-NO", label: "Norwegian Bokmål (Norway)" },
  { value: "da-DK", label: "Danish (Denmark)" },
  { value: "fi-FI", label: "Finnish (Finland)" },
  // Western / Central Europe
  { value: "nl-NL", label: "Dutch (Netherlands)" },
  { value: "nl-BE", label: "Dutch (Belgium)" },
  { value: "it-IT", label: "Italian (Italy)" },
  { value: "pt-PT", label: "Portuguese (Portugal)" },
  { value: "de-AT", label: "German (Austria)" },
  { value: "de-CH", label: "German (Switzerland)" },
  { value: "fr-CH", label: "French (Switzerland)" },
  { value: "fr-BE", label: "French (Belgium)" },
  { value: "is-IS", label: "Icelandic (Iceland)" },
  { value: "ga-IE", label: "Irish (Ireland)" },
  { value: "en-IE", label: "English (Ireland)" },
  // Eastern / Southern Europe
  { value: "pl-PL", label: "Polish (Poland)" },
  { value: "cs-CZ", label: "Czech (Czechia)" },
  { value: "sk-SK", label: "Slovak (Slovakia)" },
  { value: "hu-HU", label: "Hungarian (Hungary)" },
  { value: "ro-RO", label: "Romanian (Romania)" },
  { value: "bg-BG", label: "Bulgarian (Bulgaria)" },
  { value: "el-GR", label: "Greek (Greece)" },
  { value: "sl-SI", label: "Slovenian (Slovenia)" },
  { value: "hr-HR", label: "Croatian (Croatia)" },
  { value: "sr-Latn-RS", label: "Serbian, Latin (Serbia)" },
  { value: "et-EE", label: "Estonian (Estonia)" },
  { value: "lv-LV", label: "Latvian (Latvia)" },
  { value: "lt-LT", label: "Lithuanian (Lithuania)" },
  { value: "ru-RU", label: "Russian (Russia)" },
  { value: "uk-UA", label: "Ukrainian (Ukraine)" },
  { value: "tr-TR", label: "Turkish (Türkiye)" },
  // Americas
  { value: "en-CA", label: "English (Canada)" },
  { value: "fr-CA", label: "French (Canada)" },
  { value: "es-MX", label: "Spanish (Mexico)" },
  { value: "es-AR", label: "Spanish (Argentina)" },
  { value: "pt-BR", label: "Portuguese (Brazil)" },
  // Asia-Pacific
  { value: "en-AU", label: "English (Australia)" },
  { value: "en-NZ", label: "English (New Zealand)" },
  { value: "en-IN", label: "English (India)" },
  { value: "hi-IN", label: "Hindi (India)" },
  { value: "zh-CN", label: "Chinese, Simplified (China)" },
  { value: "zh-TW", label: "Chinese, Traditional (Taiwan)" },
  { value: "ja-JP", label: "Japanese (Japan)" },
  { value: "ko-KR", label: "Korean (South Korea)" },
  { value: "th-TH", label: "Thai (Thailand)" },
  { value: "vi-VN", label: "Vietnamese (Vietnam)" },
  { value: "id-ID", label: "Indonesian (Indonesia)" },
  { value: "ms-MY", label: "Malay (Malaysia)" },
  // Middle East / Africa
  { value: "ar-SA", label: "Arabic (Saudi Arabia)" },
  { value: "he-IL", label: "Hebrew (Israel)" },
  { value: "en-ZA", label: "English (South Africa)" },
];

export const FILE_TYPES: { value: ExportDataFileType; label: string }[] = [
  { value: "CSV", label: "CSV (Recommended)" },
  { value: "XLSX", label: "XLSX" },
];

type ExportState = "idle" | "exporting" | "done" | "error";

interface ExportDialogProps {
  name: string;
  filters: InputParam[];
  sorts: SortOrder[];
  columns: string[];
  /**
   * §6.3 wire protocol: true when `columns` equals the report's default visible set —
   * the request then omits ColumnList so the server uses its cached exportDefault.
   */
  isDefaultSelection: boolean;
  /** TEXTCOLLECTION composite → sibling columns. The server resolves composites to
   *  their siblings in the file, so the "Exporting N columns" estimate expands them. */
  columnGroups?: Record<string, string[]>;
  onClose: () => void;
}

function getStatusMessage(info: ExportInfo, dots: string): string {
  switch (info.BuildState) {
    case BuildStates.Initializing:
      return "Preparing export operation" + dots;
    case BuildStates.Downloading:
      return "Preparing data for serialization" + dots;
    case BuildStates.Building: {
      // A streaming export cannot know its size until it ends, so it reports rows written
      // instead of a fraction. Above 1 is a row count; 0..1 is the older fractional progress.
      const status = Number(info.FileBuildStatus);
      if (!Number.isFinite(status) || status <= 0) return "Serializing data" + dots;
      return status > 1
        ? `Serializing data (${Math.round(status).toLocaleString()} rows)` + dots
        : `Serializing data (${(status * 100).toFixed(0)}%)` + dots;
    }
    case BuildStates.CreatingFiles:
      return "Distributing file" + dots;
    case BuildStates.CancelRequested:
      return "Cancelling export" + dots;
    default:
      return "Processing" + dots;
  }
}

function getErrorMessage(info: ExportInfo, fileType: ExportDataFileType): string {
  switch (info.BuildState) {
    case BuildStates.UnknownError:
      return "Failed to process data-set, please try again later or using another filetype.";
    // No longer produced: exports stream, and admission is a plain slot count. Kept so a
    // status row written by an older server still reads sensibly.
    case BuildStates.ExceedTotalWeight:
      return fileType === "XLSX"
        ? "The data-set was too large to export, please try exporting as CSV or reducing the data-set."
        : "The data-set was too large to export, please try reducing the data-set.";
    case BuildStates.WaitTimeout:
      return "Every export slot was busy, please try again in a moment.";
    default:
      return "Failed to export data, please try again later.";
  }
}

async function triggerAuthDownload(path: string, authHeaders: Record<string, string>) {
  const url = path.startsWith("/") ? buildApiUrl(path) : path;
  const response = await fetch(url, { method: "GET", headers: authHeaders });
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(`Download ${response.status}: ${body || response.statusText}`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename = filenameFromContentDisposition(disposition) ?? path.split("/").pop() ?? "export";
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

const FILETYPE_STORAGE_PREFIX = "webadmincc-export-filetype-";

function loadLastFileType(userId: number | undefined): ExportDataFileType {
  if (userId == null) return "CSV";
  const raw = localStorage.getItem(FILETYPE_STORAGE_PREFIX + userId);
  return raw === "XLSX" || raw === "CSV" ? raw : "CSV";
}

function saveLastFileType(userId: number | undefined, ft: ExportDataFileType) {
  if (userId == null) return;
  localStorage.setItem(FILETYPE_STORAGE_PREFIX + userId, ft);
}

export function ExportDialog({ name, filters, sorts, columns, isDefaultSelection, columnGroups, onClose }: ExportDialogProps) {
  // Actual file column count: TEXTCOLLECTION composites yield their sibling columns,
  // not themselves (server-side expansion) — mirror that in the estimate, deduped.
  const exportedColumnCount = (() => {
    const groups = new Map<string, string[]>();
    if (columnGroups) for (const [k, v] of Object.entries(columnGroups)) groups.set(k.toLowerCase(), v);
    const out = new Set<string>();
    for (const c of columns) {
      const subs = groups.get(c.toLowerCase());
      if (subs?.length) subs.forEach((s) => out.add(s.toLowerCase()));
      else out.add(c.toLowerCase());
    }
    return out.size;
  })();
  const { session, cache } = useSession();
  const { confirm, choose } = useAlertDialog();
  const [culture, setCulture] = useState("en-US");
  const [timezone, setTimezone] = useState(() => getClientTimeZone());
  const tzOptions = useMemo(() => buildReportTimezoneOptions(cache, timezone), [cache, timezone]);
  const [fileType, setFileTypeState] = useState<ExportDataFileType>(() => loadLastFileType(session?.UID));
  const setFileType = useCallback((ft: ExportDataFileType) => {
    setFileTypeState(ft);
    saveLastFileType(session?.UID, ft);
  }, [session?.UID]);
  const [state, setState] = useState<ExportState>("idle");
  const [statusText, setStatusText] = useState("");
  const [downloadPath, setDownloadPath] = useState("");
  const [doneExportId, setDoneExportId] = useState<string>("");
  /** Rows in the file when the export finished incomplete (a database error part-way), else null. */
  const [incompleteRows, setIncompleteRows] = useState<number | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** The export being built, while it can still be cancelled. */
  const [runningExportId, setRunningExportId] = useState("");
  const dotsRef = useRef("");

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  /** An incomplete file is downloaded only once the user has said so, every time. */
  const confirmIncompleteDownload = useCallback((rows: number | null) => confirm({
    title: "Export is incomplete",
    body: incompleteExportMessage(rows),
    confirmLabel: "Download incomplete file",
    cancelLabel: "Don't download",
  }), [confirm]);

  const download = useCallback((path: string, failPrefix: string) => {
    buildAuthHeaders()
      .then((headers) => triggerAuthDownload(path, headers))
      .catch((err) => setStatusText(`${failPrefix}: ${err.message}`));
  }, []);

  /** The export is Done: download it — unless it is incomplete, which must be acknowledged first
   * and is never downloaded on its own. */
  const finishDone = useCallback(async (info: ExportInfo) => {
    setState("done");
    setDownloadPath(info.DownloadPath ?? "");
    setDoneExportId(info.ExportID);
    const incomplete = isIncompleteExport(info);
    const rows = incomplete ? incompleteExportRows(info) : null;
    setIncompleteRows(rows);
    if (!info.DownloadPath) {
      setStatusText("Export completed, but download link is missing — the file may still be finalizing. Try refreshing Exported Reports.");
      return;
    }
    if (incomplete) {
      const summary = `Export INCOMPLETE — a database error stopped it after ${rows!.toLocaleString()} rows. The file does not contain all the data.`;
      setStatusText(summary);
      if (session && (await confirmIncompleteDownload(rows))) download(info.DownloadPath, "Download failed");
      return;
    }
    setStatusText("Export completed successfully.");
    if (session) setTimeout(() => download(info.DownloadPath, "Export completed, but auto-download failed"), 1200);
  }, [session, confirmIncompleteDownload, download]);

  const handleRun = useCallback(async () => {
    // Exclude ClientSide filters from export
    const serverFilters = filters.filter((f) => f.Type !== ParamType.ClientSide);

    setState("exporting");
    setIncompleteRows(null);
    setStatusText("Contacting server...");
    dotsRef.current = "";

    try {
      const result = await exportDataGrid({
        Name: name,
        FilterList: serverFilters,
        OrderList: sorts,
        Culture: culture,
        Timezone: timezone,
        FileType: fileType,
        // Default selection -> omit ColumnList; the server synthesizes the report's
        // default export columns (memoized exportDefault keep-set, §6.3).
        ColumnList: columns.length && !isDefaultSelection ? columns : undefined,
      });

      if (result.bError || !result.oData) {
        setState("error");
        setStatusText(result.sMessage ?? "Export request failed.");
        return;
      }

      const info = result.oData;

      if (info.BuildState === BuildStates.Done) {
        await finishDone(info);
        return;
      }

      if ((info.BuildState & BuildStates.Failed) === BuildStates.Failed) {
        setState("error");
        setStatusText(getErrorMessage(info, fileType));
        return;
      }

      // Start polling
      setStatusText(getStatusMessage(info, ""));
      const exportId = info.ExportID;
      setRunningExportId(exportId);

      pollingRef.current = setInterval(async () => {
        try {
          const statusResult = await getExportStatus(exportId);
          if (!statusResult.oData) return;

          const statusInfo = statusResult.oData;

          if (statusInfo.BuildState === BuildStates.Done) {
            stopPolling();
            setRunningExportId("");
            await finishDone(statusInfo);
            return;
          }

          // Before the failure check: Cancelled carries the Failed bit, for the older clients.
          if (statusInfo.BuildState === BuildStates.Cancelled) {
            stopPolling();
            setRunningExportId("");
            setState("idle");
            setStatusText("Export cancelled. The query was stopped and no file was kept.");
            return;
          }

          if ((statusInfo.BuildState & BuildStates.Failed) === BuildStates.Failed) {
            stopPolling();
            setRunningExportId("");
            setState("error");
            setStatusText(getErrorMessage(statusInfo, fileType));
            return;
          }

          dotsRef.current = dotsRef.current.length >= 4 ? "." : dotsRef.current + ".";
          setStatusText(getStatusMessage(statusInfo, dotsRef.current));
        } catch {
          stopPolling();
          setState("error");
          setStatusText("Lost connection while checking export status.");
        }
      }, 1000);
    } catch (e) {
      setState("error");
      setStatusText(e instanceof Error ? e.message : "Export request failed.");
    }
  }, [name, filters, sorts, columns, isDefaultSelection, culture, timezone, fileType, stopPolling, finishDone]);

  /** Stops the export on the server - its query and its file. The poll reports when it has. */
  const handleCancelExport = useCallback(async () => {
    if (!runningExportId) return;
    setStatusText("Cancelling export...");
    try {
      const result = await cancelExport(runningExportId);
      // False: it had already finished; the poll is about to say how.
      if (result.bError) setStatusText(result.sMessage || "The export could not be cancelled.");
    } catch {
      setStatusText("The export could not be cancelled; it may still finish.");
    }
  }, [runningExportId]);

  // Closing while it runs leaves it running on the server unless the user says otherwise; a
  // finished file appears under Exported Reports either way.
  const handleClose = useCallback(async () => {
    if (state === "exporting" && runningExportId) {
      const choice = await choose<"keep" | "cancel">({
        title: "The export is still running",
        body: "Keep it running in the background — the file will appear under Exported Reports — or cancel it?",
        options: [
          { label: "Cancel export", value: "cancel", variant: "danger" },
          { label: "Keep running", value: "keep", variant: "primary", default: true },
        ],
        cancelLabel: "Back",
      });
      if (choice === null) return;
      if (choice === "cancel") await cancelExport(runningExportId).catch(() => undefined);
    }
    stopPolling();
    onClose();
  }, [state, runningExportId, choose, stopPolling, onClose]);

  const handleDelete = useCallback(async () => {
    if (!doneExportId) return;
    if (!(await confirm({ title: "Delete the exported file?", body: "This cannot be undone.", confirmLabel: "Delete", destructive: true }))) return;
    try {
      await deleteOwnExport(doneExportId);
      setState("idle");
      setStatusText("Exported file deleted.");
      setDownloadPath("");
      setDoneExportId("");
      setIncompleteRows(null);
    } catch (err) {
      setStatusText(`Delete failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [doneExportId]);

  const isRunning = state === "exporting";

  return (
    <Modal open onClose={handleClose}>
      <ModalHeader title="Export Grid" onClose={handleClose} />
      <ModalBody>
        <div className={styles.exportForm}>
          <label className={styles.exportLabel}>
            Culture
            <SearchableSelect
              options={CULTURES}
              value={culture}
              onChange={setCulture}
              disabled={isRunning}
              ariaLabel="Culture"
              placeholder="Search cultures…"
            />
          </label>
          <label className={styles.exportLabel}>
            File type
            <select
              className={styles.exportSelect}
              value={fileType}
              onChange={(e) => setFileType(e.target.value as ExportDataFileType)}
              disabled={isRunning}
            >
              {FILE_TYPES.map((ft) => (
                <option key={ft.value} value={ft.value}>{ft.label}</option>
              ))}
            </select>
          </label>
          <label className={styles.exportLabel}>
            Timezone
            <SearchableSelect
              options={tzOptions}
              value={timezone}
              onChange={setTimezone}
              disabled={isRunning}
              ariaLabel="Timezone"
              placeholder="Search timezone…"
            />
          </label>
          <div className={styles.exportColumnCount}>
            Exporting {exportedColumnCount} column{exportedColumnCount === 1 ? "" : "s"}
            {exportedColumnCount !== columns.length ? ` (${columns.length} selected)` : ""}
          </div>
        </div>
        {statusText && (
          <div className={`${styles.exportStatus} ${state === "error" || incompleteRows !== null ? styles.exportStatusError : state === "done" ? styles.exportStatusDone : ""}`}>
            {statusText}
            {state === "done" && downloadPath && session && (
              <div className={styles.exportDownloadLink}>
                {incompleteRows !== null ? "To download it anyway," : "If the file has not opened automatically,"}{" "}
                <a
                  href="#"
                  onClick={async (e) => {
                    e.preventDefault();
                    if (incompleteRows !== null && !(await confirmIncompleteDownload(incompleteRows))) return;
                    download(downloadPath, "Download failed");
                  }}
                >
                  {incompleteRows !== null ? "click here to download the incomplete file" : "click here to download"}
                </a>.
                {doneExportId && (
                  <>
                    {" "}Or{" "}
                    <a
                      href="#"
                      onClick={(e) => { e.preventDefault(); handleDelete(); }}
                      style={{ color: "var(--color-destructive)" }}
                    >
                      delete exported file
                    </a>.
                  </>
                )}
              </div>
            )}
            {state === "error" && (
              <div className={styles.exportDownloadLink}>
                If the problem persists, please contact support.
              </div>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button className={styles.toolbarBtn} onClick={handleClose}>Close</button>
        {isRunning && runningExportId && (
          <button className={styles.toolbarBtn} onClick={handleCancelExport}>Cancel export</button>
        )}
        <button
          className={styles.toolbarBtnPrimary}
          onClick={handleRun}
          disabled={isRunning}
        >
          {isRunning ? "Exporting..." : "Run"}
        </button>
      </ModalFooter>
    </Modal>
  );
}
